/**
 * AI-powered menu parsing via OpenAI or DeepSeek (OpenAI-compatible APIs).
 *
 * .env controls:
 *   REACT_APP_AI_PROVIDER=openai|deepseek
 *   REACT_APP_OPENAI_API_KEY=...
 *   REACT_APP_OPENAI_MODEL=gpt-4o-mini
 *   REACT_APP_DEEPSEEK_API_KEY=...
 *   REACT_APP_DEEPSEEK_MODEL=deepseek-chat
 */

const SYSTEM_PROMPT = `You are an expert restaurant menu parser.
Extract EVERY menu item from the provided menu content (PDF text, HTML, image, or URL content).

Return ONLY valid JSON (no markdown) with this exact shape:
{
  "items": [
    {
      "name": "string",
      "ingredients": "string or null",
      "price": number or null,
      "prices": { "serves10": number, "serves20": number } 
    }
  ]
}

Rules:
- "name" is the dish/item name only (e.g. "Breakfast Package"), never the ingredients list.
- "ingredients" is the description/ingredients text when present; otherwise null.
- Skip section headers like PACKAGES, APPETIZERS, ENTREES, etc.
- If there are multiple price columns (e.g. SERVES 10 / SERVES 20), put them in "prices" and set "price" to the first/primary (usually Serves 10).
- Prices must be numbers only (129 not "$129"). Never concatenate prices (never 129249).
- Preserve all real menu items; do not invent items that are not in the source.
- If a field is unknown, use null.`;

function normalizeProvider(value) {
  const p = String(value || "openai").trim().toLowerCase();
  if (p === "deepseek") return "deepseek";
  return "openai";
}

/**
 * Active AI provider + credentials from env (apiKeyOverride is optional UI key).
 */
export function getAiProviderConfig(apiKeyOverride) {
  const provider = normalizeProvider(process.env.REACT_APP_AI_PROVIDER);
  const override = (apiKeyOverride || "").trim();

  if (provider === "deepseek") {
    const apiKey =
      override || (process.env.REACT_APP_DEEPSEEK_API_KEY || "").trim();
    const model =
      process.env.REACT_APP_DEEPSEEK_MODEL || "deepseek-chat";
    const visionModel =
      process.env.REACT_APP_DEEPSEEK_VISION_MODEL ||
      "deepseek-v4-flash-vision-exp";
    const chatUrl =
      process.env.REACT_APP_DEEPSEEK_URL ||
      (process.env.NODE_ENV === "development"
        ? "/api/deepseek/chat/completions"
        : "https://api.deepseek.com/chat/completions");

    return {
      provider: "deepseek",
      label: "DeepSeek",
      apiKey,
      model,
      visionModel,
      chatUrl,
      supportsVision: true,
      envKeyName: "REACT_APP_DEEPSEEK_API_KEY",
    };
  }

  const apiKey =
    override || (process.env.REACT_APP_OPENAI_API_KEY || "").trim();
  const model = process.env.REACT_APP_OPENAI_MODEL || "gpt-4o-mini";
  const chatUrl =
    process.env.REACT_APP_OPENAI_URL ||
    (process.env.NODE_ENV === "development"
      ? "/api/openai/v1/chat/completions"
      : "https://api.openai.com/v1/chat/completions");

  return {
    provider: "openai",
    label: "OpenAI",
    apiKey,
    model,
    visionModel: model,
    chatUrl,
    supportsVision: true,
    envKeyName: "REACT_APP_OPENAI_API_KEY",
  };
}

export function getAiProvider() {
  return getAiProviderConfig().provider;
}

export function isAiParsingConfigured(apiKeyOverride) {
  return Boolean(getAiProviderConfig(apiKeyOverride).apiKey);
}

/** @deprecated use getAiProviderConfig */
export function getOpenAiApiKey(override) {
  return getAiProviderConfig(override).apiKey;
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

async function renderPdfPagesToImages(file, maxPages = 4) {
  const pdfjsLib = await import("pdfjs-dist/build/pdf");
  const pdfjs = pdfjsLib.default || pdfjsLib;
  const workerModule = await import("pdfjs-dist/build/pdf.worker.entry");
  pdfjs.GlobalWorkerOptions.workerSrc = workerModule.default || workerModule;

  const data = new Uint8Array(await file.arrayBuffer());
  const pdf = await pdfjs.getDocument({ data }).promise;
  const pageCount = Math.min(pdf.numPages, maxPages);
  const images = [];

  for (let pageNum = 1; pageNum <= pageCount; pageNum += 1) {
    const page = await pdf.getPage(pageNum);
    const viewport = page.getViewport({ scale: 1.5 });
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: context, viewport }).promise;
    images.push(canvas.toDataURL("image/jpeg", 0.85));
  }

  return images;
}

function stripHtmlToText(html) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("script, style, noscript, svg").forEach((el) => el.remove());
  return (doc.body?.innerText || doc.body?.textContent || "")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function truncate(text, max = 60000) {
  const s = String(text || "");
  if (s.length <= max) return s;
  return `${s.slice(0, max)}\n\n[Truncated]`;
}

function normalizeAiItems(rawItems) {
  if (!Array.isArray(rawItems)) return [];

  return rawItems
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const name = String(item.name || "").trim();
      if (!name) return null;

      let ingredients = item.ingredients;
      if (ingredients != null) {
        ingredients = String(ingredients).trim() || null;
      } else {
        ingredients = null;
      }

      const prices = {};
      if (item.prices && typeof item.prices === "object") {
        const s10 = Number(item.prices.serves10);
        const s20 = Number(item.prices.serves20);
        if (Number.isFinite(s10)) prices.serves10 = Number(s10.toFixed(2));
        if (Number.isFinite(s20)) prices.serves20 = Number(s20.toFixed(2));
      }

      let price =
        item.price == null || item.price === "" ? null : Number(item.price);
      if (!Number.isFinite(price)) price = null;
      else price = Number(price.toFixed(2));

      if (price != null && price >= 10000) price = prices.serves10 ?? null;
      if (prices.serves10 != null && prices.serves10 >= 10000) {
        delete prices.serves10;
      }
      if (prices.serves20 != null && prices.serves20 >= 10000) {
        delete prices.serves20;
      }

      if (price == null) {
        price = prices.serves10 ?? prices.serves20 ?? null;
      }

      const out = { name, ingredients, price };
      if (prices.serves10 != null || prices.serves20 != null) {
        out.prices = prices;
      }
      return out;
    })
    .filter(Boolean);
}

function parseJsonFromModel(content) {
  let text = String(content || "").trim();
  if (text.startsWith("```")) {
    text = text.replace(/^```(?:json)?\s*/i, "").replace(/\s*```$/, "");
  }
  const parsed = JSON.parse(text);
  if (Array.isArray(parsed)) return normalizeAiItems(parsed);
  if (parsed && Array.isArray(parsed.items)) return normalizeAiItems(parsed.items);
  return [];
}

async function callChatCompletions({
  apiKey,
  chatUrl,
  model,
  userContent,
  providerLabel,
}) {
  const res = await fetch(chatUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      model,
      temperature: 0,
      response_format: { type: "json_object" },
      messages: [
        { role: "system", content: SYSTEM_PROMPT },
        { role: "user", content: userContent },
      ],
    }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg =
      data?.error?.message ||
      `${providerLabel} request failed (${res.status}). Check your API key and model access.`;
    throw new Error(msg);
  }

  const content = data?.choices?.[0]?.message?.content;
  if (!content) throw new Error("AI returned an empty response.");
  return parseJsonFromModel(content);
}

/**
 * Parse menu content with the configured AI provider.
 */
export async function parseMenuWithAI({
  mode,
  text,
  images = [],
  sourceHint = "menu",
  apiKey: apiKeyOverride,
}) {
  const config = getAiProviderConfig(apiKeyOverride);
  if (!config.apiKey) {
    throw new Error(
      `AI parsing requires a ${config.label} API key. Set ${config.envKeyName} in .env, then restart npm start. Current provider: REACT_APP_AI_PROVIDER=${config.provider}`
    );
  }

  const wantsVision = mode === "images" && images.length > 0;
  const model = wantsVision
    ? config.visionModel || config.model
    : config.model;

  let userContent;
  if (wantsVision) {
    userContent = [
      {
        type: "text",
        text: `Parse this restaurant menu ${sourceHint} into JSON. Read item names from the ITEM column and ingredients from the INGREDIENTS column when present. Keep Serves 10 / Serves 20 prices separate.`,
      },
      ...images.map((url) => ({
        type: "image_url",
        image_url: { url, detail: "high" },
      })),
    ];
    if (text) {
      userContent.push({
        type: "text",
        text: `Additional extracted text (may help):\n${truncate(text, 20000)}`,
      });
    }
  } else {
    userContent = `Parse this restaurant menu ${sourceHint} into JSON.\n\nMENU CONTENT:\n${truncate(
      text || ""
    )}`;
  }

  return callChatCompletions({
    apiKey: config.apiKey,
    chatUrl: config.chatUrl,
    model,
    providerLabel: config.label,
    userContent,
  });
}

export async function aiParseImageFile(file, apiKey) {
  return aiParseImageFiles([file], apiKey);
}

/** One or more menu photos (e.g. multi-page board) in a single vision request. */
export async function aiParseImageFiles(files, apiKey) {
  const list = (files || []).filter(Boolean);
  if (!list.length) {
    throw new Error("No image files to parse.");
  }
  const dataUrls = await Promise.all(list.map((f) => fileToDataUrl(f)));
  const names = list.map((f) => f.name || "image").join(", ");
  return parseMenuWithAI({
    mode: "images",
    images: dataUrls,
    sourceHint: `(image${list.length > 1 ? "s" : ""}: ${names})`,
    apiKey,
  });
}

export async function aiParsePdfFile(file, apiKey, extractedText = "") {
  const images = await renderPdfPagesToImages(file, 4);
  return parseMenuWithAI({
    mode: "images",
    images,
    text: extractedText,
    sourceHint: `(pdf: ${file.name})`,
    apiKey,
  });
}

export async function aiParseTextContent(text, sourceHint, apiKey) {
  return parseMenuWithAI({
    mode: "text",
    text,
    sourceHint,
    apiKey,
  });
}

export async function aiParseHtmlContent(html, sourceHint, apiKey) {
  const text = stripHtmlToText(html);
  return aiParseTextContent(text, sourceHint || "(html menu)", apiKey);
}

export { stripHtmlToText, fileToDataUrl, renderPdfPagesToImages };
